import React, { useState } from "react";
import stilePulsanti from "../../../interfaccia/stilePulsanti";
import KitModaModal from "./KitModaModal";
import { componentiAttuali, etichettaTaglia, messaggioErrore, richiedeVerifica, statoDaConflitto } from "./kitModaApi";

/**
 * Taglie a cui si puo' applicare la composizione: proposta completa e kit gia'
 * letto da Prestashop. Quelle che non sono ancora un kit partono selezionate;
 * quelle con un kit diverso vanno scelte esplicitamente, per non sovrascrivere
 * un'eccezione voluta.
 */
export const candidateInvio = (righe) =>
  righe.filter(
    (riga) =>
      riga.inModa &&
      !richiedeVerifica(riga) &&
      riga.confronto !== "ALLINEATO" &&
      riga.proposta?.length > 0 &&
      riga.proposta.every((item) => item.tripletta),
  );

function InvioTaglieModal({ codVS, stato, api, onClose, onStato }) {
  // Elenco fissato all'apertura: dopo l'invio le taglie allineate uscirebbero
  // dalle candidate e con loro l'esito mostrato all'utente.
  const [candidate] = useState(() => candidateInvio(stato.righe));
  const [selezionate, setSelezionate] = useState(
    () => new Set(candidate.filter((riga) => riga.confronto === "DA_COMPORRE").map((riga) => riga.tripletta)),
  );
  const [esiti, setEsiti] = useState({});
  const [sending, setSending] = useState(false);
  const [finito, setFinito] = useState(false);
  const daVerificare = stato.righe.filter((riga) => riga.inModa && richiedeVerifica(riga)).length;

  const tagliaComponente = (proposta) => {
    const taglia = (stato.taglieComponenti[proposta.componenteCodVS] ?? []).find(
      (item) => item.tripletta === proposta.tripletta,
    );
    return taglia ? etichettaTaglia(taglia) : proposta.tripletta;
  };

  /** Invia una taglia; restituisce lo stato aggiornato restituito dal backend, se c'e'. */
  const inviaTaglia = async (riga, forza = false) => {
    setEsiti((attuali) => ({ ...attuali, [riga.tripletta]: { stato: "invio" } }));
    try {
      const data = await api.salvaTaglia(
        codVS,
        riga.tripletta,
        riga.proposta.map((item) => ({ tripletta: item.tripletta, quantita: item.quantita })),
        componentiAttuali(riga.cache),
        forza,
      );
      setEsiti((attuali) => ({
        ...attuali,
        [riga.tripletta]: { stato: data?.avviso ? "avviso" : "ok", messaggio: data?.avviso },
      }));
      return data;
    } catch (err) {
      const aggiornato = statoDaConflitto(err);
      setEsiti((attuali) => ({
        ...attuali,
        [riga.tripletta]: aggiornato
          ? { stato: "conflitto", messaggio: "Il kit su Prestashop è cambiato nel frattempo." }
          : { stato: "errore", messaggio: messaggioErrore(err, "Invio non riuscito") },
      }));
      return aggiornato;
    }
  };

  const invia = async () => {
    setSending(true);
    let ultimoStato = null;
    for (const riga of candidate) {
      if (!selezionate.has(riga.tripletta)) continue;
      ultimoStato = (await inviaTaglia(riga)) ?? ultimoStato;
    }
    if (ultimoStato) onStato(ultimoStato);
    setSending(false);
    setFinito(true);
  };

  const inviaComunque = async (riga) => {
    setSending(true);
    const data = await inviaTaglia(riga, true);
    if (data) onStato(data);
    setSending(false);
  };

  const footer = (
    <div className="flex justify-between items-center">
      <span className="f6 scrittaColMedio">
        {finito ? "Invio completato." : `${selezionate.size} taglie selezionate`}
      </span>
      <div>
        <button type="button" className={stilePulsanti.buttonClassesWhite} onClick={onClose} disabled={sending}>
          {finito ? "Chiudi" : "Annulla"}
        </button>
        {!finito && (
          <button
            type="button"
            className={stilePulsanti.buttonClassesGreen}
            onClick={invia}
            disabled={sending || selezionate.size === 0}
          >
            {sending ? "Invio..." : "Invia a Prestashop"}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <KitModaModal
      title="Applica la composizione alle taglie"
      subtitle="Ogni taglia selezionata riceve su Prestashop i componenti proposti: le taglie con un kit diverso restano escluse finché non le selezioni."
      onClose={onClose}
      busy={sending}
      footer={footer}
    >
      {daVerificare > 0 && (
        <p className="mt0 f6">
          {daVerificare === 1 ? "1 taglia va" : `${daVerificare} taglie vanno`} prima verificate su Prestashop e non
          compaiono qui.
        </p>
      )}
      {candidate.length === 0 ? (
        <p className="mt0">
          Nessuna taglia da aggiornare: sono già allineate, da verificare o con abbinamenti da completare a mano.
        </p>
      ) : (
        <ul className="list pl0 mt0" aria-label="Taglie da inviare">
          {candidate.map((riga) => {
            const esito = esiti[riga.tripletta];
            return (
              <li key={riga.tripletta} className="pv2 bb bordoColAlt">
                <label className="flex items-start pointer">
                  <input
                    type="checkbox"
                    className="mr2 mt1"
                    checked={selezionate.has(riga.tripletta)}
                    disabled={sending || finito}
                    onChange={(event) =>
                      setSelezionate((attuali) => {
                        const prossime = new Set(attuali);
                        if (event.target.checked) prossime.add(riga.tripletta);
                        else prossime.delete(riga.tripletta);
                        return prossime;
                      })
                    }
                  />
                  <span>
                    <span className="b">{etichettaTaglia(riga)}</span>
                    {riga.confronto === "DIVERSO" && (
                      <span className="f7 dark-red"> · ha già un kit diverso, che verrebbe sostituito</span>
                    )}
                    <span className="db f6">
                      {riga.proposta
                        .map((item) => `${item.quantita} × ${item.componenteCodVS} (${tagliaComponente(item)})`)
                        .join(" + ")}
                    </span>
                    {esito && (
                      <span
                        className={`db f6 ${["errore", "conflitto"].includes(esito.stato) ? "dark-red" : esito.stato === "ok" ? "scrittaColAltScuro" : ""}`}
                      >
                        {esito.stato === "invio" ? "Invio..." : esito.stato === "ok" ? "Inviato" : esito.messaggio}
                      </span>
                    )}
                    {esito?.stato === "conflitto" && (
                      <button
                        type="button"
                        className={`${stilePulsanti.buttonClassesWhite} mt1`}
                        onClick={() => inviaComunque(riga)}
                        disabled={sending}
                        aria-label={`Invia comunque ${riga.tripletta}`}
                      >
                        Invia comunque
                      </button>
                    )}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </KitModaModal>
  );
}

export default InvioTaglieModal;
